// src/pages/admin/ActivitySummary.jsx
// ----------------------------------------------------------------
// • Column‑level permissions: idelog.<columnKey> ( + legacy idlelog.* )
// • Pagination controls identical to UsersList page
// • Fallback to localStorage for permissions
// ----------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { API_URL } from "config";
import { useAuth } from "../../AuthContext"; // 2 dots: admin → pages → src

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DataTable from "./DataTable";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SoftButton from "components/SoftButton"; // pagination buttons
import TextField from "@mui/material/TextField";
import { Card, Icon, Typography } from "@mui/material";
import SearchableSelect from "components/SearchableSelect";
import SoftInput from "components/SoftInput";
import FlagLoader from "layouts/flagloader";

// API
import { getDepartments } from "api/departments";
import { getDesignations } from "api/designation";
import {
  getBranches,
  getManagersByBranch,
  getShiftsByBranch,
  getTeamLeadersByBranchAndShift,
} from "api/branches";
import { getRoles } from "api/roles";
import SoftPagination from "components/SoftPagination";
import SoftSelect from "components/SoftSelect";
import { roleAllowsMultipleShifts } from "utils/accessRole";

const PAGE_SIZE = 25;

/* ---------------------------------------------------------------- */
const ActivitySummary = () => {
  /* -------- permissions -------- */
  const { permissions: ctxPerms = [], isAuthReady } = useAuth();

  const storedPerms = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("permissions") || "[]");
    } catch {
      const raw = localStorage.getItem("permissions") || "";
      return raw.split(",").filter(Boolean);
    }
  }, []);

  const permNames = ctxPerms.length ? ctxPerms : storedPerms;
  const permSet = useMemo(
    () => new Set(permNames.map((p) => (typeof p === "string" ? p : p.name))),
    [permNames]
  );

  const userHas = useCallback(
    (col) =>
      permSet.has(`activitysummary.${col}`) ||
      permSet.has(`activity_summary.${col}`),
    [permSet]
  );


  /* -------- data + pagination state -------- */
  const [logs, setLogs] = useState([]);
  const [loadIng, setLoadIng] = useState(true);
  const [searchName, setSearchName] = useState("");

  const [pagination, setPag] = useState({
    page: 1,
    perPage: PAGE_SIZE,
    total: 0,
    lastPage: 1,
  });
  const [pageInput, setPageInput] = useState(1);
  // New loading states
  const [pageLoading, setPageLoading] = useState(true);   // full page loader
  const [tableLoading, setTableLoading] = useState(false); // only table loader

  // Form data
  // const [formData, setFormData] = useState({
  //   department_id: "",
  //   designation_id: "",
  //   role_id: "",
  //   branch_id: "",
  //   shift_id: null, // single shift id
  //   shifts: [], // multi shift ids
  //   manager_id: "",
  //   team_leader_id: "",
  // });

  const [formData, setFormData] = useState({
    department_id: "",
    designation_id: "",
    role_id: "",
    branch_id: "",
    shift_id: null, // single shift id
    shifts: [], // multi shift ids
    manager_id: "",
    team_leader_id: "",
    start_date: "",   // ✅ NEW
    end_date: "",     // ✅ NEW
  });


  // Dropdown data
  const [dropdowns, setDropdowns] = useState({
    department: [],
    designation: [],
    branches: [],
    roles: [],
    branchManagers: [],
    branchShifts: [],
    teamLeaders: [],
  });

  const [isMultiShift, setIsMultiShift] = useState(false);

  // Loading states
  const [loading, setLoading] = useState({
    initial: true,
    branch: false,
    teamLeaders: false,
  });

  // Load departments, designations, branches, roles
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [departmentsRes, designationsRes, branchesRes, rolesRes] = await Promise.all([
          getDepartments(1, 250),
          getDesignations(1, 250),
          getBranches(1, 250),
          getRoles(1, 250),
        ]);

        setDropdowns((prev) => ({
          ...prev,
          department: Array.isArray(departmentsRes?.data)
            ? departmentsRes.data.map((d) => ({
              label: d.name,
              value: d.id,
            }))
            : [],
          designation: Array.isArray(designationsRes?.data)
            ? designationsRes.data.map((d) => ({
              label: d.name,
              value: d.id,
            }))
            : [],
          branches: Array.isArray(branchesRes?.data)
            ? branchesRes.data.map((b) => ({
              label: b.name,
              value: b.id,
            }))
            : [],
          roles: Array.isArray(rolesRes?.roles)
            ? rolesRes.roles.map((r) => ({
              label: r.name,
              value: r.id,
              user_scope: r.user_scope,
              scopes: r.scopes || [],
            }))
            : [],
        }));
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setLoading((prev) => ({ ...prev, initial: false }));
      }
    };

    loadInitialData();
  }, []);

  // Load branch managers + shifts
  useEffect(() => {
    const loadBranchData = async () => {
      if (!formData.branch_id) {
        setDropdowns((prev) => ({
          ...prev,
          branchManagers: [],
          branchShifts: [],
          teamLeaders: [],
        }));

        setFormData((prev) => ({
          ...prev,
          manager_id: "",
          team_leader_id: "",
          shifts: [],
          shift_id: null,
        }));
        return;
      }

      setLoading((prev) => ({ ...prev, branch: true }));

      try {
        const [managersRes, shiftsRes] = await Promise.all([
          getManagersByBranch(formData.branch_id),
          getShiftsByBranch(formData.branch_id),
        ]);

        const managers = Array.isArray(managersRes?.data)
          ? managersRes.data
          : Array.isArray(managersRes)
            ? managersRes
            : [];

        const shifts = Array.isArray(shiftsRes?.data)
          ? shiftsRes.data
          : Array.isArray(shiftsRes?.shifts)
            ? shiftsRes.shifts
            : Array.isArray(shiftsRes)
              ? shiftsRes
              : [];

        setDropdowns((prev) => ({
          ...prev,
          branchManagers: managers.map((m) => ({
            label: m.name || `${m.first_name || ""} ${m.last_name || ""}`.trim(),
            value: m.id,
          })),
          branchShifts: shifts.map((s) => ({
            label: `${s.type} (${s.shift_code} ${s.start_time}-${s.end_time})`,
            value: s.id,
          })),
        }));
      } catch (error) {
        console.error("Error loading branch data:", error);
      } finally {
        setLoading((prev) => ({ ...prev, branch: false }));
      }
    };

    loadBranchData();
  }, [formData.branch_id]);

  // Load team leaders (branch + shift dependent)
  useEffect(() => {
    const loadTeamLeaders = async () => {
      if (!formData.branch_id || !formData.shift_id) {
        setDropdowns((prev) => ({ ...prev, teamLeaders: [] }));
        setFormData((prev) => ({ ...prev, team_leader_id: "" }));
        return;
      }

      setLoading((prev) => ({ ...prev, teamLeaders: true }));

      try {
        const response = await getTeamLeadersByBranchAndShift(
          formData.branch_id,
          formData.shift_id
        );
        const teamLeaders = response?.team_leaders || [];

        setDropdowns((prev) => ({
          ...prev,
          teamLeaders: teamLeaders.map((tl) => ({
            label: tl.name || "Unknown",
            value: tl.id,
          })),
        }));
      } catch (error) {
        console.error("Error loading team leaders:", error);
        setDropdowns((prev) => ({ ...prev, teamLeaders: [] }));
      } finally {
        setLoading((prev) => ({ ...prev, teamLeaders: false }));
      }
    };

    loadTeamLeaders();
  }, [formData.branch_id, formData.shift_id]);

  // Check if role allows multi-shift
  useEffect(() => {
    if (!formData.role_id) {
      setIsMultiShift(false);
      return;
    }

    const role = dropdowns.roles.find((r) => r.value === formData.role_id);

    setIsMultiShift(roleAllowsMultipleShifts(role));

    // reset shift(s) when switching modes
    setFormData((prev) => ({ ...prev, shift_id: null, shifts: [] }));
  }, [formData.role_id, dropdowns.roles]);

  // ---------------- HANDLERS ----------------
  const handleSelectChange = (field) => (option) => {
    setFormData((prev) => ({ ...prev, [field]: option?.value || "" }));
  };

  const handleMultiSelectChange = (field) => (options) => {
    setFormData((prev) => ({
      ...prev,
      [field]: options ? options.map((opt) => opt.value) : [],
    }));
  };

  // Selected role helper
  const selectedRole =
    dropdowns?.roles?.find((r) => r.value === formData.role_id)?.label?.toLowerCase() || "";

  /* -------- fetch API -------- */
  const fetchActivitySummary = useCallback(
    async (page = 1, isFilterChange = false) => {
      try {
        if (isFilterChange) {
          setTableLoading(true);   // dropdown change → sirf table loader
        } else {
          setPageLoading(true);    // first load → full page loader
        }

        const token = localStorage.getItem("authToken");

        if (!token) throw new Error("Authentication token missing");

        // base params
        let queryString = `page=${page}&per_page=${PAGE_SIZE}&q=${searchName}`;

        // date filters 
        // date filters
        if (formData.start_date) {
          queryString += `&start_date=${formData.start_date}`;
        }
        if (formData.end_date) {
          queryString += `&end_date=${formData.end_date}`;
        }


        // role filter
        if (formData.role_id && formData.role_id !== "null") {
          queryString += `&role_id=${formData.role_id}`;
        }

        // single shift filter
        if (formData.shift_id && formData.shift_id !== "null") {
          queryString += `&shift_id=${formData.shift_id}`;
        }

        // multi shift filter
        if (formData.shifts && formData.shifts.length > 0) {
          formData.shifts.forEach((id) => {
            queryString += `&shift_id[]=${id}`;
          });
        }

        // branch filter
        if (formData.branch_id && formData.branch_id !== "null") {
          queryString += `&branch_id=${formData.branch_id}`;
        }

        // department filter
        if (formData.department_id && formData.department_id !== "null") {
          queryString += `&department_id=${formData.department_id}`;
        }

        // designation filter
        if (formData.designation_id && formData.designation_id !== "null") {
          queryString += `&designation_id=${formData.designation_id}`;
        }

        // 🔍 debug log
        console.log("ActivitySummary API URL:", `${API_URL}/activity-summaries?${queryString}`);

        // fetch call
        const res = await fetch(`${API_URL}/activity-summaries?${queryString}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("API error");
        const data = await res.json();

        /* build rows */
        const apiRows = data.data || data.users?.data || data.users || [];
        const total = data.total || data.users?.total || apiRows.length;
        const lastPg = data.last_page || Math.max(1, Math.ceil(total / PAGE_SIZE));

        const rows = apiRows.map((row, idx) => {
          const user = row.user || {};
          const branch = user.branch?.name || "-";
          const shift = user.shift || {};

          return {
            no: (page - 1) * PAGE_SIZE + idx + 1,
            user: user.name || "-",
            branch,
            shiftType: shift.type || "-",
            shiftStart: shift.start_time || "-",
            shiftEnd: shift.end_time || "-",
            role: user.role || "-",

            // 👇 Activity Summary fields (from backend)
            activityDate: row.activity_date || "-",
            session: row.session || "-",
            counts: row.counts ? JSON.stringify(row.counts) : "-",
          };
        });


        setLogs(rows);
        setPag({ page, perPage: PAGE_SIZE, total, lastPage: lastPg });
      } catch (err) {
        console.error("Activity summary fetch failed:", err);
        setLogs([]);
        setPag((p) => ({ ...p, total: 0 }));
      } finally {

        setPageLoading(false);
        setTableLoading(false);
      }
    },
    [formData, searchName]
  );
  // Sync pageInput with actual page
  useEffect(() => {
    setPageInput(pagination.page);
  }, [pagination.page]);


  /* initial load */
  useEffect(() => {
    fetchActivitySummary(1, false); // false → full page loader
  }, []);

  /* when filters/pagination changes */
  useEffect(() => {
    if (!pageLoading) {
      fetchActivitySummary(pagination.page, true); // true → only table loader
    }
  }, [pagination.page, formData, searchName]);

  /* -------- column definitions -------- */
  const allColumns = [
    { key: "no", label: "No.", align: "left" },
    { key: "user", label: "User", align: "left" },
    { key: "branch", label: "Branch", align: "left" },
    { key: "shiftType", label: "Shift", align: "center" },
    { key: "shiftStart", label: "Shift Start", align: "center" },
    { key: "shiftEnd", label: "Shift End", align: "center" },
    { key: "role", label: "Role", align: "center" },
    { key: "activityDate", label: "Activity Date", align: "center" },
    { key: "session", label: "Session", align: "center" },
    { key: "counts", label: "Counts", align: "left" },
  ];


  const visibleColumns = useMemo(
    () => allColumns.filter((c) => userHas(c.key)),
    [allColumns, userHas]
  );

  const visibleRows = useMemo(
    () => logs.map((row) => Object.fromEntries(Object.entries(row).filter(([k]) => userHas(k)))),
    [logs, userHas]
  );

  /* -------- pagination handlers -------- */
  const totalPages = Math.max(1, pagination.lastPage);
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPag((prev) => ({ ...prev, page: newPage }));
    }
  };

  /* -------- render -------- */
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox p={3}>
        <SoftTypography variant="h4" fontWeight="bold" mb={2}>
          Activity Summary
        </SoftTypography>

        {/* Table or loader */}
        {pageLoading || !isAuthReady ? (
          <SoftBox display="flex" justifyContent="center" mt={4}>
            {/* <CircularProgress color="info" /> */}
            <FlagLoader />
          </SoftBox>
        ) : visibleColumns.length === 0 ? (
          <SoftTypography color="error" mt={4}>
            You do not have permission to view any columns.
          </SoftTypography>
        ) : (
          <Card style={{ flex: 1, borderRadius: "0px" }}>
            {/* Dropdown Fields */}
            <div className="container-fluid">
              <div className="row g-3 align-items-end">
                {/* Role */}
                <div className="col-md">
                  <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                    Role
                  </Typography>

                  <SoftSelect
                    className="basic-multi-select"
                    classNamePrefix="select"
                    name="roles"
                    options={dropdowns.roles}
                    isMulti={false}
                    closeMenuOnSelect={true}
                    size="small"
                    fontSize={"10px"}
                    onChange={handleSelectChange("role_id")}
                    value={dropdowns.roles.find((opt) => opt.value === formData.role_id) || null}
                  />
                </div>


                {/* Branch */}
                <div className="col-md">
                  <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                    Branch
                  </Typography>
                  <SoftSelect
                    className="basic-multi-select"
                    classNamePrefix="select"
                    name="branch_id"
                    size="small"
                    options={dropdowns.branches}
                    isMulti={false}
                    closeMenuOnSelect={true}
                    fontSize={"10px"}
                    onChange={handleSelectChange("branch_id")}
                    value={dropdowns.branches.find((opt) => opt.value === formData.branch_id) || null}
                  />
                </div>

                {/* Shift(s) */}
                <div className="col-md">
                  <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                    {isMultiShift ? "Shifts (Select Multiple)" : "Shift"}
                  </Typography>

                  {loading.branch ? (
                    <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                      Loading shifts...
                    </Typography>
                  ) : isMultiShift ? (
                    <SoftSelect
                      className="basic-multi-select"
                      classNamePrefix="select"
                      name="shift_id"
                      options={dropdowns.branchShifts}
                      isMulti={true}
                      size="small"
                      closeMenuOnSelect={true}
                      fontSize={"10px"}
                      onChange={handleMultiSelectChange("shifts")}
                      value={dropdowns.branchShifts.filter((opt) =>
                        formData.shifts?.includes(opt.value)
                      )}
                    />
                  ) : (
                    <SoftSelect
                      className="basic-multi-select"
                      classNamePrefix="select"
                      name="shift_id"
                      options={dropdowns.branchShifts}
                      isMulti={false}
                      closeMenuOnSelect={true}
                      fontSize={"10px"}
                      size="small"
                      onChange={handleSelectChange("shift_id")}
                      value={
                        dropdowns.branchShifts.find((opt) => opt.value === formData.shift_id) || null
                      }
                    />
                  )}
                </div>

                {/* Department */}
                <div className="col-md">
                  <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                    Department
                  </Typography>

                  <SoftSelect
                    className="basic-multi-select"
                    classNamePrefix="select"
                    name="department_id"
                    options={dropdowns.department}
                    isMulti={false}
                    closeMenuOnSelect={true}
                    fontSize={"10px"}
                    size="small"
                    onChange={handleSelectChange("department_id")}
                    value={
                      dropdowns.department.find((opt) => opt.value === formData.department_id) || null
                    }
                  />
                </div>

                {/* Designation */}
                <div className="col-md">
                  <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                    Designation
                  </Typography>

                  <SoftSelect
                    sx={{ borderRadius: "10px" }}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    name="designation_id"
                    options={dropdowns.designation}
                    isMulti={false}
                    size="small"
                    closeMenuOnSelect={true}
                    fontSize={"10px"}
                    onChange={handleSelectChange("designation_id")}
                    value={
                      dropdowns.designation.find((opt) => opt.value === formData.designation_id) ||
                      null
                    }
                  />
                </div>
              </div>

              <div className="row mt-3 "  >
                {/* Start Date */}
                <div className="col-md">
                  <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                    Start Date
                  </Typography>
                  <SoftInput
                    type="date"
                    size="small"
                    value={formData.start_date}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, start_date: e.target.value }))
                    }
                  />
                </div>

                {/* End Date */}
                <div className="col-md">
                  <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                    End Date
                  </Typography>
                  <SoftInput
                    type="date"
                    size="small"
                    value={formData.end_date}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, end_date: e.target.value }))
                    }
                  />
                </div>

                <div className="col-md">
                  <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                    Search
                  </Typography>
                  <SoftInput
                    variant="outlined"
                    fullWidth
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    placeholder="Enter name..."

                  />
                </div>
              </div>


            </div>



            <SoftBox display="flex" justifyContent="center" alignItems="center" my={2} gap={1}>
              <SoftBox fontWeight="small" style={{ width: "100%", fontSize: "12px" }}>
                Showing {(pagination.page - 1) * pagination.perPage + 1} to{" "}
                {Math.min(pagination.page * pagination.perPage, pagination.total)} of {pagination.total} entries
              </SoftBox>

              <SoftBox
                display="flex"
                justifyContent="end"
                alignItems="center"
                p={1}
                style={{ width: "100%", fontSize: "12px" }} // 👈 font size applied here
              >
                <SoftPagination
                  item
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.page === 1}
                  sx={{ fontSize: "12px" }} // 👈 font size
                >
                  <Icon sx={{ fontWeight: "bold", fontSize: "16px" }}>first_page</Icon>
                </SoftPagination>

                <SoftPagination
                  item
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  sx={{ fontSize: "12px" }}
                >
                  <Icon sx={{ fontWeight: "bold", fontSize: "16px" }}>chevron_left</Icon>
                </SoftPagination>

                <span
                  style={{
                    margin: "0 5px",
                    width: "120px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "12px",
                  }}
                >
                  <span width="5rem" mx={1}>
                    <SoftInput
                      inputProps={{
                        type: "number",
                        min: 1,
                        max: Math.ceil(pagination.total / pagination.perPage),
                        style: { fontSize: "12px" },
                      }}
                      value={pageInput}
                      onChange={(e) => setPageInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handlePageChange(parseInt(pageInput))}
                      onBlur={() => handlePageChange(parseInt(pageInput))}
                      sx={{ fontSize: "12px", height: "28px" }} // 👈 box size bhi chhota
                    />
                  </span>
                  /<span>{Math.ceil(pagination.total / pagination.perPage)}</span>
                </span>

                <SoftPagination
                  item
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === Math.ceil(pagination.total / pagination.perPage)}
                  sx={{ fontSize: "12px" }}
                >
                  <Icon sx={{ fontWeight: "bold", fontSize: "16px" }}>chevron_right</Icon>
                </SoftPagination>

                <SoftPagination
                  item
                  onClick={() =>
                    handlePageChange(Math.ceil(pagination.total / pagination.perPage))
                  }
                  disabled={pagination.page === Math.ceil(pagination.total / pagination.perPage)}
                  sx={{ fontSize: "12px" }}
                >
                  <Icon sx={{ fontWeight: "bold", fontSize: "16px" }}>last_page</Icon>
                </SoftPagination>
              </SoftBox>
            </SoftBox>
            {tableLoading ? (
              <SoftBox display="flex" justifyContent="center" alignItems="center" p={4}>
                {/* <CircularProgress color="info" /> */}
                <FlagLoader />
              </SoftBox>
            ) : (
              <DataTable
                table={{
                  columns: visibleColumns.map(({ key, label, align }) => ({
                    Header: label, // Table Header text
                    accessor: key, // Row object key
                    align: align || "left",
                  })),
                  rows: visibleRows,
                }}
                sx={{
                  "& .MuiTableCell-head": {
                    fontWeight: "bold !important",
                    fontSize: "0.875rem !important",
                    padding: "8px 26px !important",
                    verticalAlign: "middle",
                  },
                  "& .MuiTableCell-body": {
                    padding: "12px 16px !important",
                    verticalAlign: "middle",
                  },
                  "& .MuiTable-root": { tableLayout: "fixed" },
                }}
              />
            )}
          </Card>
        )}

      </SoftBox>
    </DashboardLayout>
  );
};

export default ActivitySummary;
